<!--
 * @Author: Christion.Young
 * @Date: 2025-07-30 10:00:00
 * @LastEditors: Christion.Young
 * @LastEditTime: 2025-08-10 13:35:00
 * @Description: PC端顶部导航栏组件 - 红色主题设计
 * @Features: 
 *   - PC端专用设计，支持响应式布局
 *   - 红色主题风格，精美动画效果
 *   - 集成搜索功能，支持键盘快捷操作
 *   - 完善的登录状态管理
 *   - 用户下拉菜单，流畅动画过渡
 *   - 聊天功能入口
 *   - 移动端适配优化
-->
<template>
  <header class="header-container">
    <div class="header-content">
      <!-- 左侧：Logo -->
      <div class="header-left">
        <img class="logo" :src="logo" alt="三火私服" @click="goHome" />
        <span class="brand-name">三火私服</span>
        <!-- 地域选择控件（自定义） -->
        <div v-if="regionFilterEnabled" class="region-selector">
          <button class="region-btn" @click="toggleRegionPanel">
            <i class="el-icon-location-outline"></i>
            <span>{{ selectedRegion.name }}</span>
            <i
              class="el-icon-arrow-down"
              :class="{ rotated: regionPanelVisible }"
            ></i>
          </button>
          <div v-if="regionPanelVisible" class="region-panel" @click.stop>
            <div class="province-list">
              <div
                v-for="province in regionCascaderOptions"
                :key="province.code"
                class="province-item"
                :class="{
                  active: province.code === selectedRegion.provinceCode,
                  'china-option': province.code === 'china',
                }"
                @mouseenter="handleProvinceHover(province)"
                @mouseleave="handleProvinceLeave"
                @click="handleProvinceClick(province)"
              >
                {{ province.name }}
              </div>
            </div>
            <div v-if="cityList.length" class="city-list">
              <div
                v-for="city in cityList"
                :key="city.code"
                class="city-item"
                :class="{ active: city.code === selectedRegion.code }"
                @click="selectCity(city)"
              >
                {{ city.name }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 中间：导航菜单 -->
      <nav class="header-nav">
        <div class="nav-menu">
          <div
            v-for="item in menuItems"
            :key="item.path"
            class="nav-item"
            :class="{ active: activeIndex === item.path }"
            @click="handleMenuSelect(item.path)"
          >
            <i :class="item.icon"></i>
            <span>{{ item.name }}</span>
          </div>
        </div>
      </nav>

      <!-- 右侧：搜索、用户操作 -->
      <div class="header-right">
        <!-- 搜索框 -->
        <div class="search-container">
          <div class="search-box" :class="{ 'search-focused': searchFocused }">
            <input
              v-model="searchKeyword"
              type="text"
              placeholder="搜索优质服务..."
              class="search-input"
              @keyup.enter="handleSearch"
              @focus="handleSearchFocus"
              @blur="handleSearchBlur"
              ref="searchInput"
            />
            <button
              class="search-btn"
              @click="handleSearch"
              :disabled="!searchKeyword.trim()"
            >
              <i class="el-icon-search"></i>
            </button>

            <!-- 搜索建议下拉 -->
            <transition name="search-dropdown">
              <div
                v-if="
                  searchFocused &&
                  (searchHistory.length > 0 || searchKeyword.trim())
                "
                class="search-suggestions"
                @mousedown.prevent
              >
                <!-- 搜索历史 -->
                <div
                  v-if="searchHistory.length > 0 && !searchKeyword.trim()"
                  class="search-section"
                >
                  <div class="search-section-title">
                    <span>最近搜索</span>
                    <button
                      class="clear-history-btn"
                      @click="clearSearchHistory"
                    >
                      <i class="el-icon-delete"></i>
                    </button>
                  </div>
                  <div
                    v-for="(item, index) in searchHistory"
                    :key="index"
                    class="suggestion-item history-item"
                    @click="selectSearchSuggestion(item)"
                  >
                    <i class="el-icon-time"></i>
                    <span>{{ item }}</span>
                  </div>
                </div>

                <!-- 当前输入 -->
                <div v-if="searchKeyword.trim()" class="search-section">
                  <div
                    class="suggestion-item current-search"
                    @click="handleSearch"
                  >
                    <i class="el-icon-search"></i>
                    <span
                      >搜索 "<strong>{{ searchKeyword.trim() }}</strong
                      >"</span
                    >
                  </div>
                </div>
              </div>
            </transition>
          </div>
        </div>

        <!-- 用户操作区域 -->
        <div class="user-actions">
          <!-- 登录/用户信息 -->
          <div v-if="!isLoggedIn" class="login-section">
            <button class="login-btn" @click="openLoginDialog">登录</button>
          </div>
          <div v-else class="user-info" @click.stop="toggleUserMenu">
            <div class="user-avatar">
              <img
                v-if="userInfo.avatarUrl"
                :src="getImageUrl(userInfo.avatarUrl)"
                alt="用户头像"
                @error="handleAvatarError"
              />
              <span v-else class="avatar-text">{{ getAvatarText() }}</span>
            </div>
            <span class="username">{{ getUserDisplayName() }}</span>
            <i
              class="el-icon-arrow-down dropdown-icon"
              :class="{ rotated: userMenuVisible }"
            ></i>

            <!-- 用户菜单下拉 -->
            <transition name="dropdown" appear>
              <div v-if="userMenuVisible" class="user-dropdown" @click.stop>
                <div
                  class="dropdown-item"
                  @click="handleUserMenuCommand('profile')"
                >
                  <i class="el-icon-user"></i>
                  <span>个人中心</span>
                </div>
                <div
                  class="dropdown-item"
                  @click="handleUserMenuCommand('chatHistory')"
                >
                  <i class="el-icon-chat-dot-round"></i>
                  <span>聊天记录</span>
                </div>
                <div class="dropdown-divider"></div>
                <div
                  class="dropdown-item"
                  @click="handleUserMenuCommand('logout')"
                >
                  <i class="el-icon-switch-button"></i>
                  <span>退出登录</span>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
import { getImageUrl } from "@/utils/imageUtils";
import territory from "./territory.json";

export default {
  name: "Header",
  data() {
    return {
      // Logo图片路径
      logo: require("@/assets/logo.png"),
      // 搜索关键词
      searchKeyword: "",
      // 当前激活的菜单项
      activeIndex: "/",
      // 用户菜单显示状态
      userMenuVisible: false,
      // 搜索框焦点状态
      searchFocused: false,
      // 搜索历史记录
      searchHistory: [],
      // 菜单项配置
      menuItems: [
        { path: "/", name: "首页", icon: "el-icon-s-home" },
        {
          path: "/merchants",
          name: "推荐商家",
          icon: "el-icon-office-building",
        },
        { path: "/services", name: "优质服务", icon: "el-icon-s-order" },
        { path: "/about", name: "关于我们", icon: "el-icon-info" },
      ],
      // 地域选择相关
      regionPanelVisible: false,
      selectedRegion: { provinceCode: "", code: "", name: "" },
      cityList: [],
      regionCascaderOptions: [], // 用于渲染省市
      hoverTimer: null, // 防抖定时器
    };
  },
  computed: {
    // 地域筛选开关（全局控制）
    regionFilterEnabled() {
      // 访问 user 模块下的 regionFilterEnabled
      const val = this.$store.getters["user/regionFilterEnabled"];
      console.log("regionFilterEnabled:", val);
      return val;
    },
    /**
     * 是否已登录
     */
    isLoggedIn() {
      return this.$store.getters["user/isLoggedIn"];
    },
    /**
     * 用户信息
     */
    userInfo() {
      return this.$store.getters["user/userInfo"] || {};
    },
  },
  watch: {
    /**
     * 监听路由变化，统一处理“推荐商家”两个路由的高亮
     */
    $route(to) {
      if (to.path.startsWith("/merchants")) {
        this.activeIndex = "/merchants"; // 统一高亮推荐商家
      } else {
        this.activeIndex = to.path;
      }
    },
  },
  created() {
    const p = this.$route.path;
    this.activeIndex = p.startsWith("/merchants") ? "/merchants" : p;
    this.loadSearchHistory();
    this.initEventListeners();
    document.addEventListener("click", this.handleGlobalClick);
    this.initTerritoryData();
    // 如果 store 中已存在选中城市中文名，直接恢复显示
    const savedCityName = this.$store.getters["user/selectedCityName"];
    if (savedCityName) {
      this.selectedRegion.name = savedCityName;
    } else {
      // 如果没有保存的城市名，保持默认的"中国"
      this.selectedRegion.name = "中国";
    }
  },
  beforeDestroy() {
    // 清除防抖定时器
    if (this.hoverTimer) {
      clearTimeout(this.hoverTimer);
    }

    // 移除事件监听
    this.removeEventListeners();
    document.removeEventListener("click", this.handleGlobalClick);
  },
  methods: {
    /**
     * 切换地域选择面板显示
     */
    toggleRegionPanel() {
      this.regionPanelVisible = !this.regionPanelVisible;
    },
    // ...existing code...
    /**
     * 切换省份
     */
    selectProvince(province) {
      this.selectedRegion.provinceCode = province.code;
      this.cityList = province.children || [];
      // 默认选中第一个城市
      if (this.cityList.length) {
        this.selectCity(this.cityList[0]);
      }
    },
    /**
     * 初始化事件监听器
     */
    initEventListeners() {
      // 监听登录成功事件
      this.$bus.on("login-success", this.handleLoginSuccess);
    },

    /**
     * 移除事件监听器
     */
    removeEventListeners() {
      this.$bus.off("login-success", this.handleLoginSuccess);
    },

    /**
     * 全局点击事件处理
     */
    handleGlobalClick(event) {
      // 检查点击是否在用户信息区域内
      const userInfoElement = this.$el.querySelector(".user-info");
      if (userInfoElement && userInfoElement.contains(event.target)) {
        return; // 如果点击在用户信息区域内，不关闭菜单
      }

      // 检查点击是否在地域选择器内
      const regionSelectorElement = this.$el.querySelector(".region-selector");
      if (
        regionSelectorElement &&
        regionSelectorElement.contains(event.target)
      ) {
        return; // 如果点击在地域选择器内，不关闭面板
      }

      // 否则关闭用户菜单和地域面板
      this.userMenuVisible = false;
      this.regionPanelVisible = false;
    },

    /**
     * 获取用户头像文字
     */
    getAvatarText() {
      const nickname = this.userInfo.nickname;
      const username = this.userInfo.username;
      if (nickname) {
        return nickname.charAt(0).toUpperCase();
      }
      if (username) {
        return username.charAt(0).toUpperCase();
      }
      return "用";
    },

    /**
     * 获取用户显示名称
     */
    getUserDisplayName() {
      return this.userInfo.nickname || this.userInfo.username || "用户";
    },

    /**
     * 获取图片URL
     */
    getImageUrl(imagePath) {
      return getImageUrl(imagePath);
    },

    /**
     * 处理头像加载错误
     */
    handleAvatarError() {
      // 头像加载失败时，移除avatarUrl属性，显示文字头像
      this.$set(this.userInfo, "avatarUrl", "");
    },

    /**
     * 切换用户菜单显示状态
     */
    toggleUserMenu() {
      this.userMenuVisible = !this.userMenuVisible;
    },

    /**
     * 点击Logo回到首页
     */
    goHome() {
      if (this.$route.path !== "/") {
        this.$router.push("/");
      }
    },

    /**
     * 处理菜单选择
     * @param {string} path - 菜单项路径
     */
    handleMenuSelect(path) {
      // 特殊处理商户列表路由 - 根据登录状态跳转不同页面
      if (path === "/merchants") {
        const targetRoute = this.isLoggedIn
          ? "/merchants/logged"
          : "/merchants";
        if (targetRoute !== this.$route.path) {
          this.$router.push(targetRoute).catch((err) => {
            console.error("菜单导航失败:", err);
          });
        }
        return;
      }

      // 其他路由正常处理
      if (path !== this.$route.path) {
        this.$router.push(path).catch((err) => {
          console.error("菜单导航失败:", err);
          // 移除用户提示，只记录错误日志
        });
      }
    },

    /**
     * 执行搜索
     */
    handleSearch() {
      const keyword = this.searchKeyword.trim();
      if (!keyword) {
        this.showMessage("请输入关键词", "warning");
        return;
      }

      // 添加到搜索历史
      this.addToSearchHistory(keyword);
      // 收起搜索建议
      this.searchFocused = false;
      // 模糊搜索框
      if (this.$refs.searchInput) {
        this.$refs.searchInput.blur();
      }

      // 跳转到搜索结果页面
      this.$router
        .push({
          name: "services",
          query: {
            keyword: keyword,
            from: "header-search",
          },
        })
        .catch((err) => {
          console.error("搜索跳转失败:", err);
          this.showMessage("搜索跳转失败，请稍后重试", "error");
        });

      // 发送搜索事件给分析统计
      this.$bus.emit("search-event", {
        keyword: keyword,
        source: "header",
      });

      // 显示搜索提示
      this.showMessage(`正在搜索"${keyword}"`, "info");
    },

    /**
     * 搜索框获得焦点
     */
    handleSearchFocus() {
      this.searchFocused = true;
    },

    /**
     * 搜索框失去焦点
     */
    handleSearchBlur() {
      // 延迟关闭，以便处理点击建议项
      setTimeout(() => {
        this.searchFocused = false;
      }, 200);
    },

    /**
     * 选择搜索建议
     */
    selectSearchSuggestion(keyword) {
      this.searchKeyword = keyword;
      this.handleSearch();
    },

    /**
     * 加载搜索历史
     */
    loadSearchHistory() {
      try {
        const history = localStorage.getItem("searchHistory");
        if (history) {
          this.searchHistory = JSON.parse(history);
        }
      } catch (error) {
        console.error("加载搜索历史失败:", error);
        this.searchHistory = [];
      }
    },

    /**
     * 添加到搜索历史
     */
    addToSearchHistory(keyword) {
      try {
        // 移除已存在的相同关键词
        this.searchHistory = this.searchHistory.filter(
          (item) => item !== keyword
        );
        // 添加到开头
        this.searchHistory.unshift(keyword);
        // 保持最多10个历史记录
        if (this.searchHistory.length > 10) {
          this.searchHistory = this.searchHistory.slice(0, 10);
        }
        // 保存到本地存储
        localStorage.setItem(
          "searchHistory",
          JSON.stringify(this.searchHistory)
        );
      } catch (error) {
        console.error("保存搜索历史失败:", error);
      }
    },

    /**
     * 清除搜索历史
     */
    clearSearchHistory() {
      this.searchHistory = [];
      try {
        localStorage.removeItem("searchHistory");
        this.showMessage("搜索历史已清除", "success");
      } catch (error) {
        console.error("清除搜索历史失败:", error);
      }
    },

    /**
     * 打开登录弹窗
     */
    openLoginDialog() {
      this.$bus.emit("show-login-dialog");
    },

    /**
     * 处理用户菜单命令
     * @param {string} command - 命令类型
     */
    handleUserMenuCommand(command) {
      this.userMenuVisible = false;
      switch (command) {
        case "profile":
          this.$router.push("/user").catch((err) => {
            console.error("跳转到用户中心失败:", err);
            // 移除用户提示，只记录错误日志
          });
          break;
        case "chatHistory":
          this.$router.push("/chat-history").catch((err) => {
            console.error("跳转到聊天记录失败:", err);
            this.showMessage("跳转失败，请稍后重试", "error");
          });
          break;
        case "logout":
          this.handleLogout();
          break;
      }
    },

    /**
     * 处理用户退出登录
     */
    async handleLogout() {
      try {
        // 使用统一消息提示工具的确认对话框
        await this.$msg.confirm("确定要退出登录吗？", "退出确认", {
          confirmButtonText: "确定退出",
          cancelButtonText: "取消",
          type: "warning",
          useCustomTheme: true,
        });

        // 用户确认退出
        this.$store.dispatch("user/logOut");
        this.showMessage("退出成功", "success");

        // 如果当前在需要登录的页面，跳转到首页
        if (this.$route.meta.requiresAuth) {
          this.$router.push("/").catch((err) => {
            console.error("退出登录后跳转首页失败:", err);
          });
        }
      } catch (error) {
        // 用户取消退出，不做任何操作
      }
    },

    /**
     * 处理登录成功事件
     */
    handleLoginSuccess() {
      this.showMessage("登录成功", "success");
    },

    /**
     * 显示消息提示
     * @param {string} message - 消息内容
     * @param {string} type - 消息类型
     */
    showMessage(message, type = "info") {
      // 使用统一消息提示工具
      switch (type) {
        case "success":
          this.$msg.success(message);
          break;
        case "warning":
          this.$msg.warning(message);
          break;
        case "error":
          this.$msg.error(message);
          break;
        case "info":
        default:
          this.$msg.info(message);
          break;
      }
    },

    /**
     * 初始化地域数据，将 territory.json 转换为省市结构
     */
    initTerritoryData() {
      const provincesRaw = territory["00"];

      // 构建省市数据
      const provinceOptions = Object.entries(provincesRaw).map(
        ([pid, pname]) => {
          const citiesRaw = territory[pid] || {};
          return {
            code: pid,
            name: pname,
            children: Object.entries(citiesRaw).map(([cid, cname]) => ({
              code: cid,
              name: cname,
            })),
          };
        }
      );

      // 在顶部添加"中国"选项
      this.regionCascaderOptions = [
        {
          code: "china",
          name: "中国",
          children: [],
        },
        ...provinceOptions,
      ];

      // 默认选中"中国"
      this.selectedRegion.provinceCode = "china";
      this.selectedRegion.code = "china";
      this.selectedRegion.name = "中国";
      this.cityList = [];

      // 默认情况下store中存储空值
      this.$store.commit("user/setSelectedCityName", "");
    },
    /**
     * 处理省份点击事件
     */
    handleProvinceClick(province) {
      if (province.code === "china") {
        // 中国选项只在点击时触发选择
        this.selectChina();
      } else {
        // 其他省份点击时不做任何操作，只通过悬浮显示城市列表
        // 城市选择通过点击城市列表中的具体城市来完成
      }
    },

    /**
     * 鼠标移入省份，切换城市列表（防抖优化）
     */
    handleProvinceHover(province) {
      // 清除之前的定时器
      if (this.hoverTimer) {
        clearTimeout(this.hoverTimer);
      }

      // 设置防抖，避免频繁切换
      this.hoverTimer = setTimeout(() => {
        // 只有当省份真的改变时才更新
        if (this.selectedRegion.provinceCode !== province.code) {
          this.selectedRegion.provinceCode = province.code;
          this.cityList = province.children || [];

          // 中国选项不在悬浮时触发选择，只更新 provinceCode 用于高亮显示
          // 其他省份正常显示城市列表
        }
      }, 150); // 150ms 防抖
    },

    /**
     * 选择中国（全国）
     */
    selectChina() {
      this.selectedRegion.provinceCode = "china";
      this.selectedRegion.code = "china";
      this.selectedRegion.name = "中国";
      this.regionPanelVisible = false;
      this.cityList = [];

      // 选择中国时，store中存储空值
      this.$store.commit("user/setSelectedCityName", "");

      // 广播地域变化事件，传递空值表示全国
      const payload = {
        province: "",
        city: "",
        code: "",
        name: "",
        changedAt: Date.now(),
      };

      const emitRegionChange = () => {
        if (this.$bus) {
          this.$bus.emit("region-changed", payload);
        }
      };

      // 若当前不在首页，则跳转首页后再发事件，确保首页已挂载监听
      if (this.$route.path !== "/") {
        this.$router
          .push("/")
          .then(() => {
            // 等待首页渲染完成再广播事件
            this.$nextTick(() => {
              emitRegionChange();
            });
          })
          .catch((err) => {
            console.error("切换城市跳转首页失败:", err);
            // 兜底：仍然发事件
            emitRegionChange();
          });
      } else {
        // 已在首页，直接广播刷新
        emitRegionChange();
      }
    },

    /**
     * 鼠标离开省份
     */
    handleProvinceLeave() {
      // 清除防抖定时器
      if (this.hoverTimer) {
        clearTimeout(this.hoverTimer);
        this.hoverTimer = null;
      }
    },
    /**
     * 选择城市
     */
    selectCity(city) {
      this.selectedRegion.code = city.code;
      this.selectedRegion.name = city.name;
      this.regionPanelVisible = false;

      // 写入 store，供首页和 API 自动携带 address
      this.$store.commit("user/setSelectedCityName", city.name);

      const payload = {
        province: this.getProvinceName(this.selectedRegion.provinceCode),
        city: city.name,
        code: city.code,
        name: city.name,
        changedAt: Date.now(),
      };

      const emitRegionChange = () => {
        if (this.$bus) {
          this.$bus.emit("region-changed", payload);
        }
      };

      // 若当前不在首页，则跳转首页后再发事件，确保首页已挂载监听
      if (this.$route.path !== "/") {
        this.$router
          .push("/")
          .then(() => {
            // 等待首页渲染完成再广播事件
            this.$nextTick(() => {
              emitRegionChange();
            });
          })
          .catch((err) => {
            console.error("切换城市跳转首页失败:", err);
            // 兜底：仍然发事件
            emitRegionChange();
          });
      } else {
        // 已在首页，直接广播刷新
        emitRegionChange();
      }
    },
    /**
     * 获取省份名称
     */
    getProvinceName(code) {
      if (code === "china") {
        return "";
      }
      const p = this.regionCascaderOptions.find((item) => item.code === code);
      return p ? p.name : "";
    },
  },
};
</script>

<style lang="scss" scoped>
// 红色主题色彩定义
$primary-red: #e74c3c;
$primary-red-dark: #c0392b;
$primary-red-light: #f39c12;
$primary-red-lighter: #ffeaa7;

// 中性色彩
$text-primary: #2c3e50;
$text-secondary: #7f8c8d;
$border-color: #e1e8ed;
$background-hover: #f8f9fa;

/* 头部容器样式 */
.header-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: linear-gradient(135deg, #fff 0%, #fafafa 100%);
  box-shadow: 0 4px 20px rgba(231, 76, 60, 0.15);
  border-bottom: 2px solid $primary-red;
}

.header-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  height: 64px;
}

/* 左侧Logo区域 */
.header-left {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 10px;

  .logo {
    height: 48px;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      transform: scale(1.02);
    }
  }

  .brand-name {
    font-size: 18px;
    font-weight: 700;
    color: $primary-red;
    letter-spacing: 0.5px;
    cursor: pointer;
    transition: color 0.2s ease;

    &:hover {
      color: $primary-red-dark;
    }
  }
}

/* 中间导航菜单 */
.header-nav {
  flex: 1;
  display: flex;
  justify-content: center;
  margin: 0 30px;

  .nav-menu {
    display: flex;
    gap: 6px;
  }

  .nav-item {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border-radius: 20px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    color: $text-primary;
    background: transparent;
    transition: all 0.2s ease;
    position: relative;

    &:hover {
      background: rgba(231, 76, 60, 0.08);
      color: $primary-red;
    }

    &.active {
      background: linear-gradient(
        135deg,
        $primary-red 0%,
        $primary-red-dark 100%
      );
      color: white;
      box-shadow: 0 2px 8px rgba(231, 76, 60, 0.3);
    }

    i {
      font-size: 16px;
    }

    span {
      white-space: nowrap;
    }
  }
}

/* 右侧操作区域 */
.header-right {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-shrink: 0;
}

/* 搜索容器 */
.search-container {
  position: relative;

  .search-box {
    display: flex;
    align-items: center;
    background: white;
    border: 1px solid $border-color;
    border-radius: 20px;
    overflow: hidden;
    transition: all 0.2s ease;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
    position: relative;

    &:hover {
      border-color: $primary-red;
      box-shadow: 0 2px 8px rgba(231, 76, 60, 0.15);
    }

    &:focus-within,
    &.search-focused {
      border-color: $primary-red;
      box-shadow: 0 2px 8px rgba(231, 76, 60, 0.2);
    }

    .search-input {
      flex: 1;
      padding: 10px 16px;
      border: none;
      outline: none;
      font-size: 13px;
      color: $text-primary;
      background: transparent;
      width: 240px;

      &::placeholder {
        color: $text-secondary;
      }
    }

    .search-btn {
      padding: 10px 14px;
      border: none;
      background: $primary-red;
      color: white;
      cursor: pointer;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      justify-content: center;

      &:hover:not(:disabled) {
        background: $primary-red-dark;
      }

      &:disabled {
        background: #ccc;
        cursor: not-allowed;
      }

      i {
        font-size: 14px;
      }
    }
  }

  /* 搜索建议下拉 */
  .search-suggestions {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 1px solid $border-color;
    border-top: none;
    border-radius: 0 0 12px 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    overflow: hidden;
    z-index: 1000;
    max-height: 300px;
    overflow-y: auto;

    .search-section {
      .search-section-title {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 8px 16px;
        background: #f8f9fa;
        border-bottom: 1px solid $border-color;
        font-size: 12px;
        color: $text-secondary;
        font-weight: 600;

        .clear-history-btn {
          padding: 2px 6px;
          background: transparent;
          border: none;
          color: $text-secondary;
          cursor: pointer;
          border-radius: 4px;
          transition: all 0.2s ease;

          &:hover {
            background: rgba(231, 76, 60, 0.1);
            color: $primary-red;
          }

          i {
            font-size: 12px;
          }
        }
      }

      .suggestion-item {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 10px 16px;
        cursor: pointer;
        transition: background-color 0.2s ease;
        font-size: 13px;
        color: $text-primary;

        &:hover {
          background: rgba(231, 76, 60, 0.05);
        }

        &.history-item {
          i {
            color: $text-secondary;
            font-size: 12px;
            width: 14px;
            text-align: center;
          }
        }

        &.current-search {
          background: rgba(231, 76, 60, 0.08);
          color: $primary-red;
          font-weight: 500;

          i {
            color: $primary-red;
            font-size: 14px;
            width: 14px;
            text-align: center;
          }

          strong {
            color: $primary-red-dark;
          }
        }

        span {
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }
  }
}

/* 用户操作区域 */
.user-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* 操作按钮通用样式 */
.action-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 14px;
  border: none;
  border-radius: 18px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s ease;
  background: transparent;
  color: $text-primary;

  &:hover {
    background: $background-hover;
  }
}

/* 聊天按钮 */
.chat-btn {
  color: $primary-red;

  &:hover {
    background: rgba(231, 76, 60, 0.08);
    color: $primary-red-dark;
  }

  i {
    font-size: 14px;
  }
}

/* 登录按钮 */
.login-btn {
  padding: 8px 18px;
  border: 1px solid $primary-red;
  border-radius: 18px;
  background: transparent;
  color: $primary-red;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: $primary-red;
    color: white;
  }
}

/* 用户信息区域 */
.user-info {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: white;
  border: 1px solid $border-color;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  .user-avatar {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: linear-gradient(
      135deg,
      $primary-red 0%,
      $primary-red-dark 100%
    );
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    flex-shrink: 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .avatar-text {
      color: white;
      font-size: 12px;
      font-weight: 600;
    }
  }

  .username {
    font-size: 14px;
    font-weight: 500;
    color: $text-primary;
    max-width: 80px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .dropdown-icon {
    font-size: 12px;
    color: $text-secondary;
    transition: transform 0.2s ease;
    flex-shrink: 0;

    &.rotated {
      transform: rotate(180deg);
    }
  }
}

/* 用户下拉菜单 */
.user-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 6px;
  background: white;
  border: 1px solid $border-color;
  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  min-width: 140px;
  z-index: 100;

  .dropdown-item {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    padding: 10px 14px;
    cursor: pointer;
    transition: background-color 0.2s ease;
    color: $text-primary;
    font-size: 13px;
    line-height: 1.4;

    &:hover {
      background: rgba(231, 76, 60, 0.08);
      color: $primary-red;
    }

    i {
      font-size: 14px;
      width: 16px;
      text-align: center;
      flex-shrink: 0;
    }

    span {
      flex: 1;
    }
  }

  .dropdown-divider {
    height: 1px;
    background: $border-color;
    margin: 2px 0;
  }
}

/* 下拉菜单动画 */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
  transform-origin: top right;
}

.dropdown-enter,
.dropdown-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(-5px);
}

.dropdown-enter-to,
.dropdown-leave {
  opacity: 1;
  transform: scale(1) translateY(0);
}

/* 搜索下拉动画 */
.search-dropdown-enter-active,
.search-dropdown-leave-active {
  transition: all 0.2s ease;
  transform-origin: top center;
}

.search-dropdown-enter,
.search-dropdown-leave-to {
  opacity: 0;
  transform: scaleY(0.8) translateY(-10px);
}

.search-dropdown-enter-to,
.search-dropdown-leave {
  opacity: 1;
  transform: scaleY(1) translateY(0);
}

/* 地域面板展示动画 */
@keyframes regionPanelShow {
  from {
    opacity: 0;
    transform: translateY(-10px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* 自定义地域选择控件样式 */
.region-selector {
  position: relative;
  margin-left: 16px;

  .region-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 18px;
    border: 1px solid $primary-red;
    border-radius: 20px;
    background: #fff;
    color: $primary-red;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(231, 76, 60, 0.08);
    transition: all 0.2s;

    &:hover {
      border-color: $primary-red-dark;
      background: $primary-red-lighter;
    }

    i {
      font-size: 16px;

      &.rotated {
        transform: rotate(180deg);
      }
    }
  }

  .region-panel {
    position: absolute;
    top: 38px;
    left: 0;
    min-width: 280px;
    background: #fff;
    border: 1px solid $primary-red;
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(231, 76, 60, 0.15);
    z-index: 999;
    padding: 16px;
    display: flex;
    gap: 16px;
    animation: regionPanelShow 0.2s ease-out;

    .province-list {
      min-width: 120px;
      max-height: 240px;
      overflow-y: auto;
      border-right: 1px solid #f0f0f0;
      padding-right: 12px;

      .province-item {
        padding: 10px 12px;
        border-radius: 8px;
        cursor: pointer;
        color: $text-primary;
        font-weight: 500;
        margin-bottom: 4px;
        transition: all 0.2s ease;
        font-size: 14px;

        &:hover {
          background: rgba(231, 76, 60, 0.1);
          color: $primary-red-dark;
        }

        &.active {
          background: $primary-red;
          color: #fff;
          font-weight: 600;
        }

        &.china-option {
          border-bottom: 1px solid #f0f0f0;
          margin-bottom: 8px;
          padding-bottom: 12px;
          font-weight: 600;

          &:hover {
            background: rgba(231, 76, 60, 0.15);
          }

          &.active {
            background: $primary-red;
            color: #fff;
          }
        }

        &:last-child {
          margin-bottom: 0;
        }
      }
    }

    .city-list {
      min-width: 120px;
      max-height: 240px;
      overflow-y: auto;

      .city-item {
        padding: 10px 12px;
        border-radius: 8px;
        cursor: pointer;
        color: $text-primary;
        font-weight: 500;
        margin-bottom: 4px;
        transition: all 0.2s ease;
        font-size: 14px;

        &:hover {
          background: rgba(231, 76, 60, 0.1);
          color: $primary-red-dark;
        }

        &.active {
          background: $primary-red;
          color: #fff;
          font-weight: 600;
        }

        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }
}

@media (max-width: 1024px) {
  .header-content {
    padding: 0 16px;
  }

  .header-nav {
    margin: 0 20px;
  }

  .search-container .search-box .search-input {
    width: 180px;
  }
}

@media (max-width: 768px) {
  .header-content {
    padding: 0 12px;
    height: 56px;
  }

  .header-left {
    .logo {
      height: 40px;
    }

    .brand-name {
      font-size: 16px;
    }
  }

  .header-nav {
    margin: 0 10px;

    .nav-item {
      padding: 6px 10px;
      font-size: 13px;

      span {
        display: none;
      }
    }
  }

  .search-container .search-box {
    .search-input {
      width: 120px;
      padding: 8px 12px;
      font-size: 12px;
    }

    .search-btn {
      padding: 8px 10px;
    }
  }

  .user-actions {
    gap: 8px;
  }

  .action-btn {
    padding: 6px 10px;
    font-size: 12px;

    span {
      display: none;
    }
  }

  .user-info {
    padding: 6px 10px;

    .username {
      display: none;
    }

    .user-avatar {
      width: 24px;
      height: 24px;

      .avatar-text {
        font-size: 11px;
      }
    }
  }
}

@media (max-width: 480px) {
  .header-nav .nav-menu {
    gap: 4px;
  }

  .search-container .search-box .search-input {
    width: 100px;
  }

  .user-dropdown {
    right: -10px;
    min-width: 120px;
  }
}
</style>
